import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import type { FiscalConfig } from '@/hooks/useFiscalConfig';

interface FiscalConfigContextType {
  fiscalConfig: FiscalConfig | null;
  loading: boolean;
  error: string | null;
  refetchConfig: () => Promise<void>;
  calculateTaxAmount: (baseAmount: number, taxRate?: number) => number;
  calculateBasePrice: (salePrice: number, taxRate?: number) => number;
  calculatePriceWithTax: (basePrice: number, taxRate?: number) => number;
}

const FiscalConfigContext = createContext<FiscalConfigContextType | null>(null);

export const useFiscalConfigContext = () => {
  const context = useContext(FiscalConfigContext);
  if (!context) {
    throw new Error('useFiscalConfigContext must be used within a FiscalConfigProvider');
  }
  return context;
};

interface FiscalConfigProviderProps {
  children: ReactNode;
  companyId?: string;
  branchId?: string;
}

export const FiscalConfigProvider: React.FC<FiscalConfigProviderProps> = ({
  children,
  companyId,
  branchId
}) => {
  const [fiscalConfig, setFiscalConfig] = useState<FiscalConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingConfig, setIsCreatingConfig] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const fetchFiscalConfig = async () => {
    if (!user || !companyId || isCreatingConfig) {
      console.log('⚠️ [Context] Missing user, companyId, or already creating config - skipping fetch');
      if (!user || !companyId) {
        setFiscalConfig(null);
        setLoading(false);
      }
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🔧 [Context] Fetching fiscal config for:', { companyId, branchId });

      // Primera consulta: buscar configuración específica (sucursal o empresa)
      let query = supabase
        .from('fiscal_configuration')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true);

      if (branchId) {
        query = query.eq('branch_id', branchId);
        console.log('🏢 [Context] Looking for branch-specific config:', branchId);
      } else {
        query = query.is('branch_id', null);
        // console.log('🏭 [Context] Looking for company-level config');
      }

      const { data, error } = await query.maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        console.log('✅ [Context] Fiscal config found and loaded:', {
          configId: data.id,
          branchId: data.branch_id,
          taxRate: data.default_tax_rate,
          taxIncluded: data.tax_included_in_prices
        });
        setFiscalConfig(data);
        return;
      }

      // Si no encontró configuración específica de sucursal, buscar configuración de empresa
      if (branchId) {
        // console.log('🔄 [Context] Branch config not found, falling back to company config');
        const { data: companyConfig, error: companyError } = await supabase
          .from('fiscal_configuration')
          .select('*')
          .eq('company_id', companyId)
          .is('branch_id', null)
          .eq('is_active', true)
          .maybeSingle();

        if (companyError && companyError.code !== 'PGRST116') {
          throw companyError;
        }

        if (companyConfig) {
          console.log('✅ [Context] Company-level fiscal config loaded:', {
            configId: companyConfig.id,
            taxRate: companyConfig.default_tax_rate,
            taxIncluded: companyConfig.tax_included_in_prices
          });
          setFiscalConfig(companyConfig);
          return;
        }
      }

      // Verificar si ya hay alguna configuración antes de crear una nueva
      const { data: existingConfigs } = await supabase
        .from('fiscal_configuration')
        .select('id')
        .eq('company_id', companyId)
        .limit(1);

      if (existingConfigs && existingConfigs.length > 0) {
        console.log('⚠️ [Context] Config exists but not matching current criteria');
        setFiscalConfig(null);
        return;
      }

      // Si llegamos aquí, no hay configuración fiscal y debemos crear una
      console.log('⚠️ [Context] No fiscal config found for company:', companyId);
      console.log('🔧 [Context] Creating default fiscal configuration');
      
      setIsCreatingConfig(true);
      
      const defaultConfig = {
        company_id: companyId,
        branch_id: branchId || null,
        tax_regime: 'general',
        default_tax_rate: 12.00,
        tax_included_in_prices: false,
        requires_fiscal_invoice: true,
        auto_calculate_tax: true,
        show_tax_breakdown: true,
        round_tax_amounts: true,
        is_active: true
      };

      const { data: newConfig, error: createError } = await supabase
        .from('fiscal_configuration')
        .insert(defaultConfig)
        .select()
        .single();

      if (createError) {
        console.error('❌ [Context] Error creating default fiscal config:', createError);
        setFiscalConfig(null);
      } else {
        console.log('✅ [Context] Default fiscal config created:', newConfig?.id);
        setFiscalConfig(newConfig);
      }

    } catch (err: any) {
      console.error('❌ [Context] Error fetching fiscal config:', err);
      setError(err.message);
      setFiscalConfig(null);
    } finally {
      setLoading(false);
      // Reset creating flag after delay to prevent immediate re-creation
      setTimeout(() => setIsCreatingConfig(false), 3000);
    }
  };

  // Funciones de cálculo
  const calculateTaxAmount = (baseAmount: number, taxRate?: number): number => {
    // Si no hay configuración fiscal, usar valores por defecto razonables
    if (!fiscalConfig) {
      console.warn('⚠️ [FiscalContext] No fiscal config available, using default tax calculation');
      const defaultRate = taxRate || 12; // 12% IVA por defecto en Guatemala
      return (baseAmount * defaultRate) / 100;
    }
    
    const rate = taxRate || fiscalConfig.default_tax_rate;
    if (!fiscalConfig.auto_calculate_tax || rate === 0) return 0;
    
    let taxAmount = 0;
    
    if (fiscalConfig.tax_included_in_prices) {
      // Si los precios YA incluyen IVA, extraer el IVA del precio total
      taxAmount = baseAmount - (baseAmount / (1 + (rate / 100)));
    } else {
      // Si los precios NO incluyen IVA, calcular el IVA a sumar
      taxAmount = (baseAmount * rate) / 100;
    }
    
    return fiscalConfig.round_tax_amounts ? Math.round(taxAmount * 100) / 100 : taxAmount;
  };

  const calculateBasePrice = (salePrice: number, taxRate?: number): number => {
    if (!fiscalConfig) return salePrice;
    
    const rate = taxRate || fiscalConfig.default_tax_rate;
    if (!fiscalConfig.tax_included_in_prices || rate === 0) return salePrice;
    
    // Extraer el precio base sin IVA del precio que ya incluye IVA
    return salePrice / (1 + (rate / 100));
  };

  const calculatePriceWithTax = (basePrice: number, taxRate?: number): number => {
    if (!fiscalConfig) return basePrice;
    
    const rate = taxRate || fiscalConfig.default_tax_rate;
    if (fiscalConfig.tax_included_in_prices || rate === 0) return basePrice;
    
    // Solo si los precios NO incluyen IVA, agregarlo
    return basePrice * (1 + (rate / 100));
  };

  // Cargar configuración inicial
  useEffect(() => {
    if (companyId && user) {
      fetchFiscalConfig();
    }
  }, [user?.id, companyId, branchId]); // Usar IDs específicos para evitar re-renders infinitos

  // Configurar actualizaciones en tiempo real
  useEffect(() => {
    if (!companyId) return;

    console.log('🔄 [Context] Setting up real-time subscription for fiscal config');

    const channel = supabase
      .channel('fiscal-config-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'fiscal_configuration',
          filter: `company_id=eq.${companyId}`
        },
        (payload) => {
          // console.log('🔄 [Context] Real-time fiscal config change:', payload);
          
          // Evitar bucle infinito cuando estamos creando configuración
          if (isCreatingConfig) {
            console.log('🔒 [Context] Ignoring real-time event while creating config');
            return;
          }
          
          const newRecord = payload.new as any;
          
          // Si el cambio afecta nuestra configuración, recargar con delay para evitar bucle
          if (newRecord && (
            (!branchId && !newRecord.branch_id) || 
            (branchId && newRecord.branch_id === branchId)
          )) {
            // Solo mostrar toast para actualizaciones, no para inserciones iniciales
            if (payload.eventType === 'UPDATE' && fiscalConfig) {
              toast({
                title: "Configuración Actualizada",
                description: "La configuración fiscal se ha actualizada automáticamente",
              });
            }
            
            // Delay más largo para evitar bucle inmediato
            setTimeout(() => {
              if (!isCreatingConfig) {
                fetchFiscalConfig();
              }
            }, 1000);
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🔄 [Context] Cleaning up real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [companyId, branchId, fiscalConfig?.id]); // Usar ID específico del fiscal config

  const contextValue: FiscalConfigContextType = {
    fiscalConfig,
    loading,
    error,
    refetchConfig: fetchFiscalConfig,
    calculateTaxAmount,
    calculateBasePrice,
    calculatePriceWithTax
  };

  return (
    <FiscalConfigContext.Provider value={contextValue}>
      {children}
    </FiscalConfigContext.Provider>
  );
};